import { useState } from "react";
import axios from "axios";
import { Loader2, Mail, MessageCircle, Send } from "lucide-react";
import { toast } from "sonner";
import { Reveal, SectionHead } from "../components/Reveal";
import { EMAIL, EMAIL_URL, WHATSAPP_DISPLAY, WHATSAPP_URL } from "../data/content";

const SUBJECTS = [
    "Conflito societário / entre sócios",
    "Conflito contratual",
    "Conflito imobiliário",
    "Gestão patrimonial / holding",
    "Agronegócio",
    "Entretenimento / influência digital",
    "Mediação",
    "Outro",
];

const EMPTY_FORM = { name: "", email: "", phone: "", subject: SUBJECTS[0], message: "" };

const inputClass =
    "w-full rounded-sm border border-cream-line bg-white px-4 py-3 text-sm text-ink outline-none transition-colors placeholder:text-graphite-text/50 focus:border-gold";

export const Contact = () => {
    const [form, setForm] = useState(EMPTY_FORM);
    const [sending, setSending] = useState(false);

    const set = (key) => (e) => setForm((f) => ({ ...f, [key]: e.target.value }));

    const submit = async (e) => {
        e.preventDefault();
        setSending(true);
        try {
            if (window.location.hostname.includes("emergentagent.com")) {
                // Preview Emergent: backend grava o lead e dispara o e-mail de alerta
                await axios.post(
                    `${process.env.REACT_APP_BACKEND_URL}/api/contact`,
                    form,
                );
            } else {
                // Produção (Netlify): Netlify Forms recebe e notifica por e-mail
                const res = await fetch("/", {
                    method: "POST",
                    headers: { "Content-Type": "application/x-www-form-urlencoded" },
                    body: new URLSearchParams({
                        "form-name": "pre-analise",
                        "bot-field": "",
                        ...form,
                    }).toString(),
                });
                if (!res.ok) throw new Error("form submit failed");
            }
            toast.success("Mensagem enviada. A CAJUSP retornará em breve.");
            setForm(EMPTY_FORM);
        } catch {
            toast.error(
                "Não foi possível enviar agora. Fale conosco diretamente pelo WhatsApp.",
            );
        } finally {
            setSending(false);
        }
    };

    return (
        <section id="contato" data-testid="contact-section" className="bg-cream py-24 sm:py-32">
            <div className="mx-auto grid max-w-7xl gap-14 px-5 sm:px-8 lg:grid-cols-12">
                <div className="lg:col-span-5">
                    <SectionHead
                        eyebrow="Contato"
                        title="Inicie uma conversa com a CAJUSP"
                        lead="Atendimento discreto desde o primeiro contato. Descreva a natureza do conflito e nossa equipe indicará o caminho procedimental adequado."
                    />

                    <div className="mt-10 space-y-4">
                        <Reveal delay={0.1}>
                            <div className="flex items-center justify-between gap-4 border border-cream-line bg-white p-5">
                                <div className="flex items-center gap-4">
                                    <span className="flex h-11 w-11 items-center justify-center rounded-sm bg-gold/10 text-gold-dark">
                                        <MessageCircle size={20} strokeWidth={1.8} />
                                    </span>
                                    <div>
                                        <p className="font-mono text-[10px] uppercase tracking-[0.24em] text-graphite-text">
                                            WhatsApp
                                        </p>
                                        <p className="mt-0.5 font-serif text-xl text-ink">
                                            {WHATSAPP_DISPLAY}
                                        </p>
                                    </div>
                                </div>
                                <a
                                    href={WHATSAPP_URL}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    data-testid="contact-whatsapp-button"
                                    className="rounded-sm bg-gold px-4 py-2.5 text-[11px] font-semibold uppercase tracking-[0.14em] text-graphite-deep transition-colors hover:bg-gold-light"
                                >
                                    Chamar
                                </a>
                            </div>
                        </Reveal>
                        <Reveal delay={0.16}>
                            <div className="flex items-center justify-between gap-4 border border-cream-line bg-white p-5">
                                <div className="flex items-center gap-4">
                                    <span className="flex h-11 w-11 items-center justify-center rounded-sm bg-gold/10 text-gold-dark">
                                        <Mail size={20} strokeWidth={1.8} />
                                    </span>
                                    <div>
                                        <p className="font-mono text-[10px] uppercase tracking-[0.24em] text-graphite-text">
                                            E-mail
                                        </p>
                                        <p className="mt-0.5 font-serif text-xl text-ink">
                                            {EMAIL}
                                        </p>
                                    </div>
                                </div>
                                <a
                                    href={EMAIL_URL}
                                    data-testid="contact-email-button"
                                    className="rounded-sm border border-cream-line px-4 py-2.5 text-[11px] font-semibold uppercase tracking-[0.14em] text-graphite-text transition-colors hover:border-gold hover:text-gold-dark"
                                >
                                    Escrever
                                </a>
                            </div>
                        </Reveal>
                    </div>
                </div>

                <div className="lg:col-span-7">
                    <Reveal delay={0.14}>
                        <form
                            onSubmit={submit}
                            data-testid="contact-form"
                            className="border border-cream-line bg-white p-7 sm:p-10"
                        >
                            <p className="font-mono text-[11px] uppercase tracking-[0.28em] text-gold-dark">
                                Pré-análise de conflito
                            </p>
                            <div className="mt-7 grid gap-5 sm:grid-cols-2">
                                <div>
                                    <label htmlFor="contact-name" className="mb-2 block text-xs font-medium uppercase tracking-[0.12em] text-graphite-text">
                                        Nome
                                    </label>
                                    <input
                                        id="contact-name"
                                        data-testid="contact-name-input"
                                        required
                                        value={form.name}
                                        onChange={set("name")}
                                        placeholder="Seu nome ou empresa"
                                        className={inputClass}
                                    />
                                </div>
                                <div>
                                    <label htmlFor="contact-email" className="mb-2 block text-xs font-medium uppercase tracking-[0.12em] text-graphite-text">
                                        E-mail
                                    </label>
                                    <input
                                        id="contact-email"
                                        data-testid="contact-email-input"
                                        type="email"
                                        required
                                        value={form.email}
                                        onChange={set("email")}
                                        placeholder="voce@empresa.com.br"
                                        className={inputClass}
                                    />
                                </div>
                                <div>
                                    <label htmlFor="contact-phone" className="mb-2 block text-xs font-medium uppercase tracking-[0.12em] text-graphite-text">
                                        Telefone (opcional)
                                    </label>
                                    <input
                                        id="contact-phone"
                                        data-testid="contact-phone-input"
                                        value={form.phone}
                                        onChange={set("phone")}
                                        placeholder="(11) 90000-0000"
                                        className={inputClass}
                                    />
                                </div>
                                <div>
                                    <label htmlFor="contact-subject" className="mb-2 block text-xs font-medium uppercase tracking-[0.12em] text-graphite-text">
                                        Natureza do conflito
                                    </label>
                                    <select
                                        id="contact-subject"
                                        data-testid="contact-subject-select"
                                        value={form.subject}
                                        onChange={set("subject")}
                                        className={inputClass}
                                    >
                                        {SUBJECTS.map((s) => (
                                            <option key={s} value={s}>
                                                {s}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div className="sm:col-span-2">
                                    <label htmlFor="contact-message" className="mb-2 block text-xs font-medium uppercase tracking-[0.12em] text-graphite-text">
                                        Resumo do caso
                                    </label>
                                    <textarea
                                        id="contact-message"
                                        data-testid="contact-message-input"
                                        required
                                        rows={5}
                                        value={form.message}
                                        onChange={set("message")}
                                        placeholder="Descreva brevemente o contexto, as partes envolvidas e o valor estimado em discussão."
                                        className={`${inputClass} resize-none`}
                                    />
                                </div>
                            </div>
                            <button
                                type="submit"
                                data-testid="contact-submit-button"
                                disabled={sending}
                                className="mt-8 inline-flex w-full items-center justify-center gap-3 rounded-sm bg-gold px-6 py-4 text-xs font-semibold uppercase tracking-[0.16em] text-graphite-deep transition-colors hover:bg-gold-light disabled:cursor-not-allowed disabled:opacity-60"
                            >
                                {sending ? (
                                    <Loader2 size={16} className="animate-spin" />
                                ) : (
                                    <Send size={16} strokeWidth={2.2} />
                                )}
                                {sending ? "Enviando…" : "Solicitar pré-análise"}
                            </button>
                            <p className="mt-4 text-center text-xs text-graphite-text">
                                As informações enviadas são tratadas com discrição.
                            </p>
                        </form>
                    </Reveal>
                </div>
            </div>
        </section>
    );
};
